import api from 'api';
import { getPublicationTimestampForElasticQuery } from 'lib/utils/dates';
import { apiPost } from 'api/typed';
import { NewspaperOrderStatus } from 'lib/types/newspaperOrder';
import {
  ElasticNoticeSearchResults,
  ElasticOrderSearchResults,
  SORT_DESCENDING,
  SearchableNewspaperOrderRecord,
  SearchableNewspaperOrderRecordFilter,
  SearchableNoticeRecord,
  SearchablePublicationIssueSectionRecord,
  SearchablePublicationIssueSectionRecordFilter,
  SearchableNoticeRecordFilter
} from 'lib/types/searchable';
import { ColumnService } from 'lib/services/directory';
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  addDays
} from 'date-fns';
import { ERequestTypes } from 'lib/types';
import { logAndCaptureException } from 'utils';
import { PublicationIssueStatus } from 'lib/types/publicationIssue';
import { GetDateRangeFn } from '../context/ReportParamsContext';
import { SELECT_DATE_RANGES } from '../constants/dateRanges';

export const getDateRangeFromSelection = (
  dateRange: string
): { startDate: string; endDate: string } => {
  const now = new Date();
  const yesterday = subDays(now, 1);
  const lastMonth = subMonths(now, 1);

  switch (dateRange) {
    case SELECT_DATE_RANGES.custom.value:
      return { startDate: '', endDate: '' };
    case SELECT_DATE_RANGES.today.value:
      return {
        startDate: startOfDay(now).toISOString(),
        endDate: endOfDay(now).toISOString()
      };
    case SELECT_DATE_RANGES.tomorrow.value:
      return {
        startDate: startOfDay(addDays(now, 1)).toISOString(),
        endDate: endOfDay(addDays(now, 1)).toISOString()
      };
    case SELECT_DATE_RANGES.yesterday.value:
      return {
        startDate: startOfDay(yesterday).toISOString(),
        endDate: endOfDay(yesterday).toISOString()
      };
    case SELECT_DATE_RANGES.last7days.value:
      return {
        startDate: startOfDay(subDays(now, 7)).toISOString(),
        endDate: endOfDay(now).toISOString()
      };
    case SELECT_DATE_RANGES.thisMonth.value:
      return {
        startDate: startOfMonth(now).toISOString(),
        endDate: endOfMonth(now).toISOString()
      };
    case SELECT_DATE_RANGES.lastMonth.value:
      return {
        startDate: startOfMonth(lastMonth).toISOString(),
        endDate: endOfMonth(lastMonth).toISOString()
      };
    case SELECT_DATE_RANGES.last30days.value:
    default:
      return {
        startDate: startOfDay(subDays(now, 30)).toISOString(),
        endDate: endOfDay(now).toISOString()
      };
  }
};

export async function fetchNewspaperOrdersFromElastic(
  newspaperIds: string[],
  options: { startDate: string | undefined; endDate: string | undefined }
): Promise<SearchableNewspaperOrderRecord[]> {
  const { startDate, endDate } = options;
  if (!newspaperIds.length || !startDate || !endDate) {
    return [];
  }
  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });
  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;
  const filters: SearchableNewspaperOrderRecordFilter[] = [
    { publisherid: newspaperIds },
    { isactive: [1] },
    { newspaperorderstatus: [NewspaperOrderStatus.COMPLETE] },
    { lastpublishingdate: { from: startTimestamp, to: endTimestamp } }
  ];

  const pageSize = 1000;
  let pageNumber = 1;
  let allResults: SearchableNewspaperOrderRecord[] = [];
  const MAX_PAGES = 10;

  for (let i = 0; i < MAX_PAGES; i++) {
    const params: ERequestTypes['search/orders'] = {
      search: '',
      filters,
      pageSize,
      pageNumber,
      sort: [
        { createdat: SORT_DESCENDING } as Record<string, typeof SORT_DESCENDING>
      ]
    };
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await apiPost('search/orders', params);
      if (!data || data.success === false || !data.results) {
        throw new Error('Failed to fetch newspaper orders');
      }

      if (!data.results.length) {
        break;
      }

      allResults = allResults.concat(data.results);
      if (data.results.length < pageSize) {
        break;
      }

      pageNumber += 1;
    } catch (error) {
      logAndCaptureException(
        ColumnService.ELASTIC,
        error as Error,
        'Failed to fetch newspaper orders',
        { startDate, endDate }
      );
      throw error;
    }
  }

  if (pageNumber >= MAX_PAGES) {
    logAndCaptureException(
      ColumnService.ELASTIC,
      new Error(
        `Reached maximum page limit (${MAX_PAGES}) while fetching newspaper orders`
      ),
      '[Column Reports V2] Reached maximum page limit while fetching newspaper orders in ProductsTab'
    );
  }
  return allResults;
}

export const fetchNoticesFromElastic = async (
  orgIds: string[],
  options: { startDate: string | undefined; endDate: string | undefined }
): Promise<SearchableNoticeRecord[]> => {
  const { startDate, endDate } = options;
  if (!orgIds.length || !startDate || !endDate) {
    return [];
  }
  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });
  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;
  const filters: SearchableNoticeRecordFilter[] = [
    { publisherid: orgIds } as any,
    { isdraft: [0] },
    { iscancelled: [0] },
    { lastpublicationtimestamp: { from: startTimestamp, to: endTimestamp } }
  ];

  const pageSize = 1000;
  let pageNumber = 1;
  let allResults: SearchableNoticeRecord[] = [];
  const MAX_PAGES = 10;

  for (let i = 0; i < MAX_PAGES; i++) {
    const params: ERequestTypes['search/usernotices'] = {
      search: '',
      filters,
      size: pageSize,
      current: pageNumber,
      isPublisher: true,
      activeOrganizationId: undefined,
      specifiedOrganizationIds: orgIds,
      onlyExplicitFilters: true
    };
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await apiPost('search/usernotices', params);

      if (!data || data.error || !data.results) {
        throw new Error('Failed to fetch notices');
      }

      if (!data.results.length) {
        break;
      }

      allResults = allResults.concat(data.results);

      if (data.results.length < pageSize) {
        break;
      }

      pageNumber += 1;
    } catch (error) {
      logAndCaptureException(
        ColumnService.ELASTIC,
        error as Error,
        'Failed to fetch notices',
        { startDate, endDate }
      );
      throw error;
    }
  }

  if (pageNumber >= MAX_PAGES) {
    logAndCaptureException(
      ColumnService.ELASTIC,
      new Error(
        `Reached maximum page limit (${MAX_PAGES}) while fetching notices`
      ),
      '[Column Reports V2] Reached maximum page limit while fetching notices in NoticesTab'
    );
  }

  return allResults;
};

const getSearchParamsFromContext = (
  getOrgIds: () => string[],
  getDateRange: GetDateRangeFn
) => {
  const orgIds = getOrgIds();
  const {
    dateRange,
    startDate: customStartDate,
    endDate: customEndDate
  } = getDateRange();

  const { startDate: rangeStartDate, endDate: rangeEndDate } =
    getDateRangeFromSelection(dateRange);

  const startDate =
    dateRange === SELECT_DATE_RANGES.custom.value
      ? customStartDate
      : rangeStartDate;

  const endDate =
    dateRange === SELECT_DATE_RANGES.custom.value
      ? customEndDate
      : rangeEndDate;

  return { orgIds, startDate, endDate };
};

export const fetchOrdersWithParams = (
  getOrgIds: () => string[],
  getDateRange: GetDateRangeFn,
  withLoading: (p: Promise<any>) => Promise<any>
): Promise<SearchableNewspaperOrderRecord[]> => {
  const { orgIds, startDate, endDate } = getSearchParamsFromContext(
    getOrgIds,
    getDateRange
  );

  return withLoading(
    fetchNewspaperOrdersFromElastic(orgIds, { startDate, endDate })
  );
};

export const fetchNoticesWithParams = (
  getOrgIds: () => string[],
  getDateRange: GetDateRangeFn,
  withLoading: (p: Promise<any>) => Promise<any>
): Promise<SearchableNoticeRecord[]> => {
  const { orgIds, startDate, endDate } = getSearchParamsFromContext(
    getOrgIds,
    getDateRange
  );

  return withLoading(fetchNoticesFromElastic(orgIds, { startDate, endDate }));
};

const fetchPublicationIssueSectionsFromElastic = async (
  orgIds: string[],
  {
    startDate,
    endDate
  }: { startDate: string | undefined; endDate: string | undefined }
): Promise<SearchablePublicationIssueSectionRecord[]> => {
  if (!orgIds.length || !startDate || !endDate) {
    return [];
  }
  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });
  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;

  const filters: SearchablePublicationIssueSectionRecordFilter[] = [
    { publisherid: orgIds },
    { publicationtimestamp: { from: startTimestamp, to: endTimestamp } },
    {
      status: Object.values(PublicationIssueStatus).filter(value =>
        [
          PublicationIssueStatus.APPROVED,
          PublicationIssueStatus.AWAITING_APPROVAL,
          PublicationIssueStatus.CHANGES_REQUESTED,
          PublicationIssueStatus.READY_FOR_PAGINATION
        ].includes(value)
      )
    }
  ];

  const pageSize = 1000;
  let pageNumber = 1;
  let allResults: SearchablePublicationIssueSectionRecord[] = [];
  const MAX_PAGES = 10;

  while (pageNumber <= MAX_PAGES) {
    const params: ERequestTypes['search/publication-issue-sections'] = {
      search: '',
      filters,
      pageSize,
      pageNumber,
      sort: [
        { deadlinetimestamp: SORT_DESCENDING } as Record<
          string,
          typeof SORT_DESCENDING
        >
      ]
    };

    // eslint-disable-next-line no-await-in-loop
    const { error, response } = await api.post(
      'search/publication-issue-sections',
      params
    );

    if (error || !response) {
      const err =
        error || new Error('Failed to fetch publication issue sections');
      logAndCaptureException(
        ColumnService.ELASTIC,
        err,
        'Failed to fetch publication issue sections',
        { startDate, endDate }
      );
      throw err;
    }

    allResults = allResults.concat(response.results);
    pageNumber += 1;

    if (response.results.length < pageSize) {
      break;
    }
  }

  if (pageNumber > MAX_PAGES) {
    logAndCaptureException(
      ColumnService.ELASTIC,
      new Error(
        `Reached maximum page limit (${MAX_PAGES}) while fetching publication-issue-sections`
      ),
      '[Column Reports V2] Reached maximum page limit while fetching publication issue sections in Publication Issues Tab'
    );
  }
  return allResults;
};

// Helper function to fetch publication issue sections with params
export const fetchPublicationIssueSectionsWithParams = (
  getOrgIds: () => string[],
  getDateRange: GetDateRangeFn,
  withLoading: (p: Promise<any>) => Promise<any>
) => {
  const { orgIds, startDate, endDate } = getSearchParamsFromContext(
    getOrgIds,
    getDateRange
  );

  return withLoading(
    fetchPublicationIssueSectionsFromElastic(orgIds, { startDate, endDate })
  );
};

export const fetchProductCategoryAggregations = async (
  getOrgIds: () => string[],
  getDateRange: GetDateRangeFn
): Promise<{
  orders: ElasticOrderSearchResults;
  notices: ElasticNoticeSearchResults;
} | null> => {
  const { orgIds, startDate, endDate } = getSearchParamsFromContext(
    getOrgIds,
    getDateRange
  );

  if (!orgIds.length || !startDate || !endDate) {
    return null;
  }

  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });

  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;

  const { error, response } = await api.safePost(
    'search/product-category-aggregations',
    {
      orgIds,
      startTimestamp,
      endTimestamp
    }
  );

  if (error) {
    throw error;
  }

  return response;
};

export const fetchCustomerNoticesAndOrdersAggregations = async (
  getOrgIds: () => string[],
  getDateRange: GetDateRangeFn
): Promise<{
  orders: ElasticOrderSearchResults;
  notices: ElasticNoticeSearchResults;
} | null> => {
  const { orgIds, startDate, endDate } = getSearchParamsFromContext(
    getOrgIds,
    getDateRange
  );

  if (!orgIds.length || !startDate || !endDate) {
    return null;
  }

  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });

  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;

  const { error, response } = await api.safePost(
    'search/customer-notices-and-orders-aggregations',
    {
      orgIds,
      startTimestamp,
      endTimestamp
    }
  );

  if (error) {
    throw error;
  }

  return response;
};

export const fetchRevenueByMonthAggregation = async (
  getOrgIds: () => string[],
  getDateRange: GetDateRangeFn
): Promise<{
  orders: ElasticOrderSearchResults;
  notices: ElasticNoticeSearchResults;
} | null> => {
  const { orgIds, startDate, endDate } = getSearchParamsFromContext(
    getOrgIds,
    getDateRange
  );

  if (!orgIds.length || !startDate || !endDate) {
    return null;
  }

  const startTimestamp = getPublicationTimestampForElasticQuery({
    dayString: startDate
  });

  const endTimestamp =
    getPublicationTimestampForElasticQuery({ dayString: endDate }) + 1;

  const { error, response } = await api.safePost(
    'search/notices-and-orders-by-month-aggregations',
    {
      orgIds,
      startTimestamp,
      endTimestamp
    }
  );

  if (error) {
    throw error;
  }

  if (!response) {
    throw new Error(
      'Failed to fetch notices and orders revenue by month aggregations'
    );
  }

  return response;
};
